<template>
  <el-dialog
    :title="dialogTitle || 'PCBA销售合作协议'"
    :visible.sync="dialogVisible"
    :close-on-click-modal="false"
    :append-to-body="true"
    custom-class="pdf-preview-dialog"
    width="1000px"
    center
    @close="handleClose"
  >
    <div class="pdf-preview-container">
      <div v-if="pdfBlobUrl && !loading" class="pdf-viewer">
        <!-- 使用blob URL预览PDF，不会触发下载 -->
        <iframe 
          :src="pdfBlobUrl" 
          class="pdf-iframe"
          @load="handleIframeLoad"
          @error="handleIframeError"
        ></iframe>
      </div>
      <div v-else-if="loadError" class="pdf-error">
        <i class="el-icon-warning"></i>
        <span>PDF加载失败，请检查文件地址</span>
        <el-button type="text" @click="retryLoad">重试</el-button>
      </div>
      <div v-else class="pdf-loading">
        <i class="el-icon-loading"></i>
        <span>{{ loading ? 'PDF加载中...' : '暂无PDF内容' }}</span>
      </div>
    </div>
    <div slot="footer" class="pdf-dialog-footer">
      <el-button 
        type="primary" 
        @click="downloadPdf"
        :loading="downloadLoading"
        :disabled="!pdfUrl"
      >
        同意并下载
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { getToken } from '@@/utils/auth'

export default {
  name: 'PdfPreviewDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    pdfUrl: {
      type: String,
      default: ''
    },
    fileName: {
      type: String,
      default: 'document.pdf'
    },
    dialogTitle: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      downloadLoading: false,
      pdfBlobUrl: null,
      loadError: false,
      pdfBlob: null // 存储PDF的blob数据，用于下载
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    },
    // 显示的文件名
    displayFileName() {
      if (this.fileName && this.fileName !== 'document.pdf') {
        return this.fileName
      }
      
      if (this.pdfUrl) {
        const urlParts = this.pdfUrl.split('/')
        return urlParts[urlParts.length - 1] || 'document.pdf'
      }
      
      return 'document.pdf'
    }
  },
  watch: {
    visible(newVal) {
      if (newVal && this.pdfUrl) {
        this.loadPdfBlob()
      } else if (!newVal) {
        this.cleanupBlobUrl()
      }
    },
    pdfUrl(newVal) {
      if (newVal && this.visible) {
        this.loadPdfBlob()
      }
    }
  },
  methods: {
    // 加载PDF的blob数据
    async loadPdfBlob() {
      if (!this.pdfUrl) return
      if (this.loading) return // 防止重复请求
      
      this.loading = true
      this.loadError = false
      this.cleanupBlobUrl()
      
      try {
        const token = getToken() || localStorage.getItem('token')
        const headers = {}
        if (token) {
          headers['Authorization'] = token
        }
        
        const response = await fetch(this.pdfUrl, { headers })
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        
        const blob = await response.blob()
        
        // 验证是否为PDF文件
        if (!blob.type.includes('pdf') && !this.pdfUrl.toLowerCase().includes('.pdf')) {
          throw new Error('文件不是PDF格式')
        }
        
        // 存储blob用于下载
        this.pdfBlob = blob
        
        // 创建blob URL用于预览
        this.pdfBlobUrl = `${window.URL.createObjectURL(blob)}#toolbar=0&navpanes=0&scrollbar=0&sidebarViewOnLoad=0`
        
        this.loading = false
        
      } catch (error) {
        console.error('PDF加载失败:', error)
        this.loading = false
        this.loadError = true
        this.$message.error('PDF加载失败: ' + error.message)
      }
    },

    // 清理blob URL
    cleanupBlobUrl() {
      if (this.pdfBlobUrl) {
        window.URL.revokeObjectURL(this.pdfBlobUrl)
        this.pdfBlobUrl = null
      }
      this.pdfBlob = null
    },

    // 重试加载
    retryLoad() {
      this.loadPdfBlob()
    },

    // 处理弹窗关闭
    handleClose() {
      this.$emit('close')
      this.loading = false
      this.downloadLoading = false
      this.cleanupBlobUrl()
    },

    // 处理iframe加载完成
    handleIframeLoad() {
      // iframe加载完成，但我们已经在loadPdfBlob中处理了loading状态
    },

    // 处理iframe加载错误
    handleIframeError() {
      this.loadError = true
      this.$message.error('PDF预览失败')
    },

    // 下载PDF文件
    async downloadPdf() {
      if (!this.pdfUrl) {
        this.$message.error('PDF地址不能为空')
        return
      }

      try {
        this.downloadLoading = true
        
        // 发出下载开始事件
        this.$emit('download-start', this.pdfUrl)
        
        let blob = this.pdfBlob
        
        // 如果没有缓存的blob，重新获取
        if (!blob) {
          const token = getToken() || localStorage.getItem('token')
          const headers = {}
          if (token) {
            headers['Authorization'] = token
          }
          
          const response = await fetch(this.pdfUrl, { headers })
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }
          blob = await response.blob()
        }
        
        // 使用传入的文件名或从URL中提取文件名
        let downloadFileName = this.fileName
        if (!downloadFileName || downloadFileName === 'document.pdf') {
          const urlParts = this.pdfUrl.split('/')
          downloadFileName = urlParts[urlParts.length - 1] || 'document.pdf'
        }
        
        // 确保文件名有.pdf扩展名
        if (!downloadFileName.toLowerCase().endsWith('.pdf')) {
          downloadFileName += '.pdf'
        }
        
        // 创建下载链接
        const blobUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = downloadFileName
        
        // 添加到DOM，触发下载，然后移除
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        // 清理下载用的blob URL
        window.URL.revokeObjectURL(blobUrl)
        
        
        // 发出下载成功事件
        this.$emit('download-success', {
          url: this.pdfUrl,
          fileName: downloadFileName
        })
        
      } catch (error) {
        console.error('下载失败:', error)
        this.$message.error('下载失败，请重试')
        
        // 发出下载失败事件
        this.$emit('download-error', {
          url: this.pdfUrl,
          error: error.message
        })
      } finally {
        this.downloadLoading = false
      }
    },

    // 公开方法：显示PDF预览
    show(url, fileName) {
      if (url) {
        this.$emit('update:visible', true)
        // 如果传入了新的URL，更新pdfUrl
        if (url !== this.pdfUrl) {
          this.$emit('update:pdfUrl', url)
        }
        // 如果传入了文件名，更新fileName
        if (fileName) {
          this.$emit('update:fileName', fileName)
        }
      } else {
        this.$message.error('PDF地址不能为空')
      }
    },

    // 公开方法：隐藏PDF预览
    hide() {
      this.dialogVisible = false
    }
  },
  
  // 组件销毁时清理blob URL
  beforeDestroy() {
    this.cleanupBlobUrl()
  }
}
</script>

<style lang="scss" scoped>
.pdf-preview-container {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
  
  .pdf-viewer {
    width: 100%;
    height: 100%;
  }
  
  .pdf-iframe {
    width: 100%;
    height: 600px;
    border: none;
    border-radius: 4px;
  }
  
  .pdf-error {
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 500px;
    justify-content: center;
    gap: 15px;
    color: #f56c6c;
    font-size: 16px;
    
    i {
      font-size: 32px;
    }
    
    span {
      font-weight: 500;
    }
    
    .el-button {
      margin-top: 10px;
    }
  }
  
  .pdf-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 500px;
    gap: 15px;
    color: #666;
    font-size: 16px;
    
    i {
      font-size: 32px;
      animation: rotating 2s linear infinite;
      color: #409EFF;
    }
    
    span {
      font-weight: 500;
    }
  }
}

.pdf-dialog-footer {
  text-align: center;
  padding: 15px 0;
  
  
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>

<style>
/* PDF预览弹窗全局样式 */
.pdf-preview-dialog {
  max-height: 90vh !important;
  margin-top: 5vh !important;
  margin-bottom: 5vh !important;
}

.pdf-preview-dialog .el-dialog {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.pdf-preview-dialog .el-dialog__header {
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  padding: 16px 20px;
  flex-shrink: 0;
}

.pdf-preview-dialog .el-dialog__title {
  font-size: 18px;
  color: #2c3e50;
}

.pdf-preview-dialog .el-dialog__close {
  font-size: 20px;
  color: #6c757d;
}

.pdf-preview-dialog .el-dialog__close:hover {
  color: #495057;
}

.pdf-preview-dialog .el-dialog__body {
  padding: 0 !important;
  background: #ffffff;
  overflow: auto;
}

.pdf-preview-dialog .el-dialog__footer {
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
  padding: 0;
  flex-shrink: 0;
}

/* 响应式设计 */
@media (max-width: 850px) {
  .pdf-preview-dialog {
    width: 95% !important;
    margin: 2.5vh auto !important;
    max-height: 95vh !important;
  }
}

@media (max-width: 768px) {
  .pdf-preview-dialog {
    width: 95% !important;
    margin: 2.5vh auto !important;
    max-height: 95vh !important;
  }
  
  .pdf-preview-dialog .el-dialog__header {
    padding: 12px 16px;
  }
  
  .pdf-preview-dialog .el-dialog__title {
    font-size: 16px;
  }
  
  
  .pdf-dialog-footer .el-button {
    padding: 8px 16px;
    font-size: 14px;
    min-width: 120px;
  }
}

@media (max-width: 480px) {
  .pdf-preview-dialog {
    width: 100% !important;
    margin: 0 !important;
    max-height: 100vh !important;
  }
  
  .pdf-preview-dialog .el-dialog {
    border-radius: 0;
    height: 100vh;
  }
  
  .pdf-preview-container {
    height: 300px !important;
  }
}
</style>